import { Checkbox, FormControl, ListItemText, MenuItem, Select } from '@mui/material';
import { useEffect, useState } from 'react';
import { PROPERTY_TYPE_GROUPS } from 'util/Constants';
import ClearFilterButton from './ClearFilterButton';

interface MultiplePropertyTypeFilterProps {
    placeholder: string;
    value: string[];
    onChanged: (value: string[]) => void;
    showClearButton?: boolean;
    displayClassName?: string;
    'data-cy'?: string;
}

const MultiplePropertyTypeFilter = ({
    value,
    onChanged,
    placeholder,
    showClearButton = true,
    displayClassName = '',
    'data-cy': dataCy,
}: MultiplePropertyTypeFilterProps) => {
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    useEffect(() => {
        if (value && Array.isArray(value)) {
            setSelectedValues(value);
        } else {
            setSelectedValues([]);
        }
    }, [value]);

    const handleChange = (event: any) => {
        const selectedValue = event.target.value;

        // Ensure we have a valid array of strings
        const newSelectedValues = Array.isArray(selectedValue)
            ? selectedValue.filter((val) => val !== undefined && val !== null)
            : [selectedValue].filter((val) => val !== undefined && val !== null);

        setSelectedValues(newSelectedValues);
        onChanged(newSelectedValues);
    };

    const clearFilterClicked = () => {
        setSelectedValues([]);
        onChanged([]);
    };

    const renderValue = (selected: unknown) => {
        if (!selectedValues || selectedValues.length === 0) {
            return placeholder;
        }
        return selectedValues.join(', ');
    };

    return (
        <div className="relative w-full h-full">
            <FormControl fullWidth className="!h-full">
                <Select
                    multiple
                    value={selectedValues}
                    onChange={handleChange}
                    displayEmpty
                    renderValue={renderValue}
                    className={`!w-full !h-full rounded-[8px] border-[#DFE1E1] leading-6 px-1 ${displayClassName}`}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {PROPERTY_TYPE_GROUPS.map((group) => (
                        <div key={group.name}>
                            <MenuItem
                                value={group.name}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const isAllSelected = group.types.every((v) => selectedValues.includes(v));
                                    let newValues: string[];

                                    if (isAllSelected) {
                                        // Remove all types from this group
                                        newValues = selectedValues.filter((v) => !group.types.includes(v));
                                    } else {
                                        // Add all types from this group
                                        newValues = [...selectedValues, ...group.types.filter((v) => !selectedValues.includes(v))];
                                    }

                                    setSelectedValues(newValues);
                                    onChanged(newValues);
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                <Checkbox
                                    checked={group.types.every((v) => selectedValues.includes(v))}
                                    indeterminate={group.types.some((v) => selectedValues.includes(v)) && !group.types.every((v) => selectedValues.includes(v))}
                                    sx={{
                                        padding: '0 8px 0 0',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '20px',
                                        },
                                    }}
                                />
                                <ListItemText
                                    primary={group.name}
                                    sx={{
                                        m: 0,
                                        '& .MuiTypography-root': {
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                            </MenuItem>
                            {group.types.map((type) => (
                                <MenuItem
                                    key={type}
                                    value={type}
                                    data-cy={dataCy}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const isSelected = selectedValues.includes(type);
                                        let newValues: string[];

                                        if (isSelected) {
                                            // Remove this type
                                            newValues = selectedValues.filter((v) => v !== type);
                                        } else {
                                            // Add this type
                                            newValues = [...selectedValues, type];
                                        }

                                        setSelectedValues(newValues);
                                        onChanged(newValues);
                                    }}
                                    sx={{
                                        pl: 4,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedValues.includes(type)}
                                        sx={{
                                            padding: '0 8px 0 0',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '20px',
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={type}
                                        sx={{
                                            m: 0,
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </div>
                    ))}
                </Select>
            </FormControl>

            {/* clear button */}
            {value && value.length > 0 && showClearButton && <ClearFilterButton onClicked={clearFilterClicked} />}
        </div>
    );
};

export default MultiplePropertyTypeFilter;
